/**
 * Phase 3a — 디바이스 라벨 자동 감지 (Refs: #1112, Epic #1110).
 *
 * navigator.userAgent + WebAuthn authenticatorAttachment로 사용자 친화 이름 생성.
 * 사용자는 Settings 페이지에서 라벨을 자유롭게 수정 가능 (U10 결정).
 */

const PLATFORM_AUTHENTICATORS = {
  Mac: 'Touch ID',
  iPhone: 'Face ID',
  iPad: 'Face ID',
  Windows: 'Windows Hello',
  Android: 'Android Biometric',
  Linux: 'Biometric',
}

/**
 * Phase 3a 후속 (Issue #1161) — UA 에서 OS 버전 추출.
 *
 * 본 함수는 PRF 호환성 판단 (services/passkey/deviceCompatibility.js) 의 보조로 사용.
 * UA spoof 가능성은 UX 결정용이므로 영향 없음 — 정확도가 떨어지면 보수적(null)으로 처리.
 *
 * 패턴:
 *  - iOS/iPadOS: 'CPU OS 17_5_1' / 'CPU iPhone OS 18_4' → underscore → dot
 *  - macOS: 'Mac OS X 14_5' / 'Mac OS X 10_15_7'
 *  - Android: 'Android 14' / 'Android 9.0'
 *  - Windows NT 10.0: null (Q7=W1 — Win10/11 UA 구분 불가, 옵티미스틱 호환)
 *  - 미매칭: null (Q9=V1 — 호환 가정에 사용)
 *
 * @param {string|null|undefined} ua - navigator.userAgent
 * @returns {string|null} 버전 문자열 ('17.5.1', '14.5', '14') 또는 null
 */
// Hotfix #1173 — macOS UA Freezing 회귀 차단.
// macOS 11+ 의 모든 주요 브라우저가 UA 단순화 정책으로 macOS 부분을 frozen 표기 (2026-05-22 실측).
// 정확한 OS 버전은 navigator.userAgentData.getHighEntropyValues(['platformVersion'])
// (Chromium 한정) 로만 조회 가능 — UA 파싱 단계에서는 frozen 시그니처를 null 로 표현하여
// V1 옵티미스틱 (호환 가정) 분기로 fallback. 정확도 향상은 후속 PR 에서.
//
// 출처:
//   - Chromium UA Reduction: https://www.chromium.org/updates/ua-reduction/
//   - WebKit "Updates to WebKit's User Agent String": https://webkit.org/blog/12299/
//   - Mozilla bug 1679929 (UA 단순화)
//
// 패턴:
//   - WebKit (Safari) / Chromium (Chrome/Edge): 'Mac OS X 10_15_7' → '10.15.7'
//   - Mozilla (Firefox): 'Mac OS X 10.15' (patch 생략)
//   - iPad Safari 데스크탑 모드: 동일하게 'Mac OS X 10_15_7' 표기 (iPadOS 13+ 기본)
//
// false positive 정책 — 진짜 macOS Catalina (10.15.x) 사용자:
//   현재 모든 브라우저가 macOS 11+ 를 요구 (Chrome 134+, Safari 18+, Firefox 144+) 하므로
//   사실상 frozen 시그니처로만 존재. 가능성 zero 가정이 깨지면 isPlatformAuthenticatorAvailable()
//   가 실제 차단 책임을 짐 (Catalina 는 Touch ID + Safari 14 한정).
const FROZEN_MACOS_VERSIONS = new Set(['10.15.7', '10.15'])

export function parseOsVersion(ua) {
  if (!ua) return null
  // iOS/iPadOS — 'CPU OS 17_5_1' 또는 'CPU iPhone OS 18_4' (underscore → dot)
  const ios = ua.match(/CPU(?: iPhone)? OS (\d+(?:_\d+)*)/)
  if (ios) return ios[1].replace(/_/g, '.')
  // macOS — 'Mac OS X 14_5' (underscore) 또는 'Mac OS X 14.5' (dot)
  const mac = ua.match(/Mac OS X (\d+(?:[_.]\d+)*)/)
  if (mac) {
    const version = mac[1].replace(/_/g, '.')
    // #1173: frozen 시그니처는 null 반환 — V1 옵티미스틱 분기로 fallback
    if (FROZEN_MACOS_VERSIONS.has(version)) return null
    return version
  }
  // Android — 'Android 14' / 'Android 9.0'
  const android = ua.match(/Android (\d+(?:\.\d+)*)/)
  if (android) return android[1]
  // Windows NT 10.0 등은 Win10/11 구분 불가 — Q7=W1 옵티미스틱 (null 반환)
  return null
}

/**
 * semver 단순 비교 — null 한쪽이라도 있으면 0 (V1 옵티미스틱 호환 가정).
 *
 * @param {string|null} a
 * @param {string|null} b
 * @returns {number} -1 if a < b, 0 if eq 또는 한쪽 null, 1 if a > b
 */
export function compareOsVersion(a, b) {
  if (!a || !b) return 0
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i += 1) {
    const av = pa[i] || 0
    const bv = pb[i] || 0
    if (av < bv) return -1
    if (av > bv) return 1
  }
  return 0
}

export function parsePlatform(ua) {
  if (!ua) return 'Device'
  const lower = ua.toLowerCase()
  if (lower.includes('iphone')) return 'iPhone'
  if (lower.includes('ipad')) return 'iPad'
  if (lower.includes('mac os x') || lower.includes('macintosh')) return 'Mac'
  if (lower.includes('windows')) return 'Windows'
  if (lower.includes('android')) return 'Android'
  if (lower.includes('linux')) return 'Linux'
  return 'Device'
}

export function parseBrowser(ua) {
  if (!ua) return 'Browser'
  const lower = ua.toLowerCase()
  // Edge / Whale / Samsung 등은 Chrome 문자열도 포함하므로 먼저 검사
  if (lower.includes('edg/')) return 'Edge'
  if (lower.includes('whale/')) return 'Whale'
  if (lower.includes('samsungbrowser')) return 'Samsung Internet'
  if (lower.includes('firefox/')) return 'Firefox'
  if (lower.includes('opr/') || lower.includes('opera')) return 'Opera'
  if (lower.includes('chrome/')) return 'Chrome'
  if (lower.includes('safari/')) return 'Safari'
  return 'Browser'
}

/**
 * OS별 PRF 지원 저장 위치 안내 i18n key 반환 (Refs: #1130).
 * 브라우저명은 별도 변수로 전달하여 i18n 보간 ({{browser}}).
 *
 * @param {string} platform - parsePlatform() 결과
 * @returns {string} i18n key (passkey.providerGuidance.X)
 */
export function getRecommendedProvidersI18nKey(platform) {
  switch (platform) {
    case 'Mac':
      return 'passkey.providerGuidance.macos'
    case 'Windows':
      return 'passkey.providerGuidance.windows'
    case 'iPhone':
    case 'iPad':
      return 'passkey.providerGuidance.ios'
    case 'Android':
      return 'passkey.providerGuidance.android'
    default:
      return 'passkey.providerGuidance.generic'
  }
}

/**
 * Phase 3a hotfix — PasskeyRegisterModal 의 사후 에러 (prfNotSupportedAction) description.
 *
 * 기존 retryHint 단일 키는 macOS Chrome 가정. iPad 사용자에게는 부적절한 "Chrome 프로필" 안내 표시.
 * platform 별 분기로 정확한 안내 제공.
 *
 * @param {string} platform - parsePlatform() 결과
 * @returns {string} i18n key (passkey.error.retryHint.X)
 */
export function getRetryHintI18nKey(platform) {
  switch (platform) {
    case 'Mac':
      return 'passkey.error.retryHint.macos'
    case 'Windows':
      return 'passkey.error.retryHint.windows'
    case 'iPhone':
    case 'iPad':
      return 'passkey.error.retryHint.ios'
    case 'Android':
      return 'passkey.error.retryHint.android'
    default:
      return 'passkey.error.retryHint.generic'
  }
}

/**
 * @param {string} ua - navigator.userAgent
 * @param {string|null|undefined} authenticatorAttachment - 'platform' | 'cross-platform' | null
 * @returns {string} 사용자 라벨 (예: 'Mac · Safari · Touch ID')
 */
export function autoDetectDeviceLabel(ua, authenticatorAttachment) {
  const platform = parsePlatform(ua)
  const browser = parseBrowser(ua)
  if (authenticatorAttachment === 'cross-platform') {
    return `${platform} · ${browser} · Security Key`
  }
  const auth = PLATFORM_AUTHENTICATORS[platform] || 'Passkey'
  return `${platform} · ${browser} · ${auth}`
}

// ---------------------------------------------------------------------------
// Issue #1176 — UA Client Hints 정밀화 (Chromium 한정)
// ---------------------------------------------------------------------------
//
// macOS Chrome/Edge 등 Chromium 계열에서 navigator.userAgentData.getHighEntropyValues
// 로 진짜 OS 버전 + 플랫폼 조회하여 #1174 frozen UA 화이트리스트 fallback 의 정확도 향상.
// Safari/Firefox/iOS 모든 브라우저 미지원 → fallback (UA 파싱 + frozen 화이트리스트) 유지.
//
// 출처:
//   - W3C UA Client Hints: https://wicg.github.io/ua-client-hints/#sec-ch-ua-platform
//   - MDN NavigatorUAData.getHighEntropyValues: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/getHighEntropyValues
//
// 주의 1 (iOS dead code): iOS 모든 브라우저 (Safari/Chrome/Firefox) 가 WebKit 강제로 userAgentData 미지원.
//   현재 매핑 'iOS' → 'iPhone' 은 사실상 dead code. iPad/iPhone 의 NATIVE_PRF_REQUIREMENTS.minOsVersion
//   동일 (18.0) 이라 잘못 매핑되어도 PRF 호환성 판단 영향 zero.
//   TODO: Apple 이 미래 iOS 에 userAgentData 도입 + iPad/iPhone PRF 정책 분리 시,
//         maxTouchPoints 휴리스틱으로 iPad 추정하여 정확 분류 검토.
//
// 주의 2 (미매핑 platform fallback 의도): 미매핑 platform (Unknown/ChromeOS 등) 입력 시 null 반환.
//   호출처 (refineFromUaClientHints) 가 || fallbackPlatform 으로 UA 파싱 결과 사용 — 의도적 설계.
//   사유: 매핑 안 되는 platform 은 UA 파싱이 더 풍부할 수 있음. 미래에 매핑 확장 시 본 함수 수정.
const UA_DATA_PLATFORM_MAP = {
  macOS: 'Mac',
  Windows: 'Windows',
  Android: 'Android',
  Linux: 'Linux',
  iOS: 'iPhone', // 위 주의 1 참고
}

/**
 * W3C UA Client Hints 의 navigator.userAgentData.platform 표준값을 우리 platform 으로 매핑.
 *
 * @param {string|null|undefined} uaDataPlatform - "macOS" / "Windows" / "iOS" / "Android" / "Linux" / "Unknown" 등
 * @returns {string|null} 'Mac' / 'Windows' / 'iPhone' / 'Android' / 'Linux' 또는 null (미매핑)
 */
export function mapUaDataPlatform(uaDataPlatform) {
  if (!uaDataPlatform || typeof uaDataPlatform !== 'string') return null
  return UA_DATA_PLATFORM_MAP[uaDataPlatform] || null
}

/**
 * UA Client Hints 로 정밀화된 (platform, osVersion) 반환. 미지원/실패 시 fallback 유지.
 *
 * @param {{platform: string, osVersion: string|null}} fallback - UA 파싱 1차 결과
 * @returns {Promise<{platform: string, osVersion: string|null}>}
 */
export async function refineFromUaClientHints(fallback) {
  if (typeof navigator === 'undefined' || !navigator.userAgentData?.getHighEntropyValues) {
    return fallback
  }
  try {
    const ch = await navigator.userAgentData.getHighEntropyValues(['platformVersion'])
    const chPlatform = mapUaDataPlatform(navigator.userAgentData.platform)
    return {
      platform: chPlatform || fallback.platform,
      osVersion: ch?.platformVersion || fallback.osVersion,
    }
  } catch (e) {
    // NotAllowedError / Permissions-Policy 차단 — 1차 결과 유지 + 디버깅용 로깅
    // eslint-disable-next-line no-console
    console.warn('[passkey] userAgentData.getHighEntropyValues failed; using fallback', e)
    return fallback
  }
}
